import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { useCart } from '../context/CartContext';
import { CONTACT_INFO, CARD_DESCRIPTOR, FREE_SHIPPING_THRESHOLD, SHIPPING_TIME, SHIPPING_METHOD } from '../data/mock';
import { ArrowLeft, CreditCard, Lock, Truck, ShieldCheck, Glasses, Check, MapPin, Home } from 'lucide-react';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCart } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);

  const [formData, setFormData] = useState({
    // Contact
    email: '',
    phone: '',
    // Shipping address
    shipFirstName: '',
    shipLastName: '',
    shipAddress: '',
    shipApartment: '',
    shipCity: '',
    shipState: '',
    shipZipCode: '',
    shipCountry: 'United States',
    // Billing address
    billFirstName: '',
    billLastName: '',
    billAddress: '',
    billApartment: '',
    billCity: '',
    billState: '',
    billZipCode: '',
    billCountry: 'United States',
    // Payment
    cardNumber: '',
    expiry: '',
    cvv: '',
    nameOnCard: '',
  });

  useEffect(() => {
    document.title = 'Checkout | Clarity Lens Co';
  }, []);

  const subtotal = cartTotal;
  const shipping = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : 7.99;
  const total = subtotal + shipping;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      alert('Please agree to the Terms & Conditions to continue.');
      return;
    }
    setIsProcessing(true);

    // Simulate payment processing
    setTimeout(() => {
      clearCart();
      navigate('/order-confirmation');
    }, 2000);
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <CartDrawer />
        <main className="pt-[105px]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Glasses className="w-8 h-8 text-neutral-400" />
            </div>
            <h1 className="text-2xl font-semibold text-neutral-900 mb-2">Your Cart is Empty</h1>
            <p className="text-neutral-600 mb-6">Add some eyewear to your cart before checkout.</p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-6 py-3 bg-neutral-900 text-white font-medium rounded-xl hover:bg-copper-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Continue Shopping
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const inputClass =
    'w-full px-4 py-3 border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10 bg-white';

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <CartDrawer />

      <main className="pt-[105px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* Back Link */}
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Continue Shopping
          </Link>

          <div className="grid lg:grid-cols-5 gap-10">
            {/* Checkout Form */}
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Contact Information */}
                <div className="bg-white border border-neutral-200 rounded-2xl p-6">
                  <h2 className="font-semibold text-neutral-900 text-lg mb-4">Contact Information</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Email</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className={inputClass}
                        placeholder="your@email.com"
                        data-testid="checkout-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={inputClass}
                        placeholder="(555) 555-5555"
                        data-testid="checkout-phone"
                      />
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="bg-white border border-neutral-200 rounded-2xl p-6" data-testid="shipping-address-section">
                  <h2 className="font-semibold text-neutral-900 text-lg mb-4 flex items-center gap-2">
                    <Truck className="w-5 h-5" />
                    Shipping Address
                  </h2>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">First Name</label>
                        <input
                          type="text"
                          name="shipFirstName"
                          required
                          value={formData.shipFirstName}
                          onChange={handleInputChange}
                          className={inputClass}
                          data-testid="ship-first-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Last Name</label>
                        <input
                          type="text"
                          name="shipLastName"
                          required
                          value={formData.shipLastName}
                          onChange={handleInputChange}
                          className={inputClass}
                          data-testid="ship-last-name"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Street Address</label>
                      <input
                        type="text"
                        name="shipAddress"
                        required
                        value={formData.shipAddress}
                        onChange={handleInputChange}
                        className={inputClass}
                        placeholder="123 Main Street"
                        data-testid="ship-address"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Apartment, suite, etc. (optional)
                      </label>
                      <input
                        type="text"
                        name="shipApartment"
                        value={formData.shipApartment}
                        onChange={handleInputChange}
                        className={inputClass}
                        data-testid="ship-apartment"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">City</label>
                        <input
                          type="text"
                          name="shipCity"
                          required
                          value={formData.shipCity}
                          onChange={handleInputChange}
                          className={inputClass}
                          data-testid="ship-city"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">State</label>
                        <select
                          name="shipState"
                          required
                          value={formData.shipState}
                          onChange={handleInputChange}
                          className={inputClass}
                          data-testid="ship-state"
                        >
                          <option value="">Select</option>
                          {US_STATES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">ZIP Code</label>
                        <input
                          type="text"
                          name="shipZipCode"
                          required
                          value={formData.shipZipCode}
                          onChange={handleInputChange}
                          className={inputClass}
                          placeholder="90001"
                          data-testid="ship-zip"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Country</label>
                      <input
                        type="text"
                        name="shipCountry"
                        value={formData.shipCountry}
                        readOnly
                        className={`${inputClass} bg-neutral-50 text-neutral-600`}
                      />
                      <p className="text-xs text-neutral-500 mt-1">We currently ship only within the United States.</p>
                    </div>
                  </div>
                </div>

                {/* Billing Address */}
                <div className="bg-white border border-neutral-200 rounded-2xl p-6" data-testid="billing-address-section">
                  <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                    <h2 className="font-semibold text-neutral-900 text-lg flex items-center gap-2">
                      <Home className="w-5 h-5" />
                      Billing Address
                    </h2>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={billingSameAsShipping}
                          onChange={(e) => setBillingSameAsShipping(e.target.checked)}
                          className="sr-only"
                          data-testid="billing-same-as-shipping"
                        />
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            billingSameAsShipping ? 'bg-neutral-900 border-neutral-900' : 'border-neutral-300 bg-white'
                          }`}
                        >
                          {billingSameAsShipping && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                      <span className="text-sm text-neutral-700 font-medium">Same as shipping address</span>
                    </label>
                  </div>

                  {billingSameAsShipping ? (
                    <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-4 flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-neutral-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-neutral-700 leading-relaxed">
                        <p className="font-medium text-neutral-900 mb-1">Your billing address matches your shipping address.</p>
                        <p className="text-neutral-600">
                          Uncheck the box above if your billing address (the address on file with your card issuer) is
                          different.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">First Name</label>
                          <input
                            type="text"
                            name="billFirstName"
                            required
                            value={formData.billFirstName}
                            onChange={handleInputChange}
                            className={inputClass}
                            data-testid="bill-first-name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">Last Name</label>
                          <input
                            type="text"
                            name="billLastName"
                            required
                            value={formData.billLastName}
                            onChange={handleInputChange}
                            className={inputClass}
                            data-testid="bill-last-name"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Street Address</label>
                        <input
                          type="text"
                          name="billAddress"
                          required
                          value={formData.billAddress}
                          onChange={handleInputChange}
                          className={inputClass}
                          placeholder="123 Main Street"
                          data-testid="bill-address"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          Apartment, suite, etc. (optional)
                        </label>
                        <input
                          type="text"
                          name="billApartment"
                          value={formData.billApartment}
                          onChange={handleInputChange}
                          className={inputClass}
                          data-testid="bill-apartment"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">City</label>
                          <input
                            type="text"
                            name="billCity"
                            required
                            value={formData.billCity}
                            onChange={handleInputChange}
                            className={inputClass}
                            data-testid="bill-city"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">State</label>
                          <select
                            name="billState"
                            required
                            value={formData.billState}
                            onChange={handleInputChange}
                            className={inputClass}
                            data-testid="bill-state"
                          >
                            <option value="">Select</option>
                            {US_STATES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">ZIP Code</label>
                          <input
                            type="text"
                            name="billZipCode"
                            required
                            value={formData.billZipCode}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="90001"
                            data-testid="bill-zip"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Country</label>
                        <input
                          type="text"
                          name="billCountry"
                          value={formData.billCountry}
                          readOnly
                          className={`${inputClass} bg-neutral-50 text-neutral-600`}
                        />
                        <p className="text-xs text-neutral-500 mt-1">
                          Billing address must match the address on file with your card issuer.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment */}
                <div className="bg-white border border-neutral-200 rounded-2xl p-6">
                  <h2 className="font-semibold text-neutral-900 text-lg mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Payment
                  </h2>

                  {/* We Accept - Card Logos */}
                  <div className="mb-6 pb-6 border-b border-neutral-200">
                    <p className="text-sm text-neutral-600 mb-3">We Accept</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      {/* Visa */}
                      <div className="bg-white border border-neutral-200 rounded-lg px-3 py-2">
                        <svg className="h-6 w-auto" viewBox="0 0 750 471" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="750" height="471" rx="40" fill="#1A1F71" />
                          <path d="M278.198 334.228L311.058 138.432H364.028L331.158 334.228H278.198ZM524.308 142.688C513.958 138.792 497.468 134.528 477.168 134.528C424.888 134.528 388.028 162.108 387.698 201.628C387.368 231.208 414.618 247.698 435.248 257.388C456.538 267.408 463.778 273.688 463.668 282.618C463.558 296.218 447.288 302.278 432.088 302.278C411.018 302.278 399.798 299.038 382.428 291.358L375.518 288.008L367.928 333.038C380.418 338.618 403.378 343.468 427.228 343.688C482.808 343.688 518.888 316.548 519.328 274.568C519.548 250.828 504.568 232.658 472.818 217.718C453.728 208.238 441.898 201.848 442.008 192.258C442.008 183.658 451.918 174.398 473.198 174.398C490.898 174.068 503.828 177.858 514.068 181.758L518.998 184.008L526.478 140.408L524.308 142.688ZM661.628 138.432H620.028C607.208 138.432 597.628 141.888 591.708 154.598L513.628 334.228H569.098L580.318 304.208H647.878L654.238 334.228H703.198L661.628 138.432ZM596.208 264.878C600.538 253.698 617.578 208.458 617.578 208.458C617.248 209.008 621.798 197.278 624.448 189.818L628.008 206.858C628.008 206.858 638.568 255.598 640.888 264.878H596.208ZM232.898 138.432L181.068 267.958L175.578 241.698C166.098 210.098 136.958 175.768 104.208 158.618L151.578 333.898H207.488L288.478 138.432H232.898Z" fill="white" />
                          <path d="M131.918 138.432H45.7784L45.0084 142.578C112.188 159.728 156.548 199.908 175.578 241.698L156.108 154.928C152.658 142.328 143.188 138.872 131.918 138.432Z" fill="#F9A51A" />
                        </svg>
                      </div>
                      {/* Mastercard */}
                      <div className="bg-white border border-neutral-200 rounded-lg px-3 py-2">
                        <svg className="h-6 w-auto" viewBox="0 0 152 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="152" height="100" rx="8" fill="#ffffff" />
                          <circle cx="60" cy="50" r="30" fill="#EB001B" />
                          <circle cx="92" cy="50" r="30" fill="#F79E1B" />
                          <path d="M76 25.6C83.3 31.3 88 40.1 88 50C88 59.9 83.3 68.7 76 74.4C68.7 68.7 64 59.9 64 50C64 40.1 68.7 31.3 76 25.6Z" fill="#FF5F00" />
                        </svg>
                      </div>
                      {/* American Express */}
                      <div className="bg-white border border-neutral-200 rounded-lg px-3 py-2">
                        <svg className="h-6 w-auto" viewBox="0 0 750 471" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="750" height="471" rx="40" fill="#006FCF" />
                          <text x="140" y="290" fill="white" fontFamily="Arial" fontWeight="bold" fontSize="110">AMEX</text>
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Name on Card</label>
                      <input
                        type="text"
                        name="nameOnCard"
                        required
                        value={formData.nameOnCard}
                        onChange={handleInputChange}
                        className={inputClass}
                        data-testid="name-on-card"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Card Number</label>
                      <input
                        type="text"
                        name="cardNumber"
                        required
                        value={formData.cardNumber}
                        onChange={handleInputChange}
                        className={inputClass}
                        placeholder="1234 5678 9012 3456"
                        data-testid="card-number"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Expiry Date</label>
                        <input
                          type="text"
                          name="expiry"
                          required
                          value={formData.expiry}
                          onChange={handleInputChange}
                          className={inputClass}
                          placeholder="MM/YY"
                          data-testid="card-expiry"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">CVV</label>
                        <input
                          type="text"
                          name="cvv"
                          required
                          value={formData.cvv}
                          onChange={handleInputChange}
                          className={inputClass}
                          placeholder="123"
                          data-testid="card-cvv"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-neutral-500 flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Your payment info is secured with SSL encryption
                    </p>
                  </div>
                </div>

                {/* Terms & Conditions Checkbox */}
                <div className="bg-white border border-neutral-200 rounded-2xl p-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <div className="relative flex-shrink-0 mt-0.5">
                      <input
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        className="sr-only"
                        data-testid="agree-terms"
                      />
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          agreedToTerms ? 'bg-neutral-900 border-neutral-900' : 'border-neutral-300 bg-white'
                        }`}
                      >
                        {agreedToTerms && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                    <div className="text-sm text-neutral-600 leading-relaxed">
                      <span className="font-medium text-neutral-900">
                        I agree to the{' '}
                        <Link to="/terms" className="text-copper-600 hover:underline">
                          Terms & Conditions
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="text-copper-600 hover:underline">
                          Privacy Policy
                        </Link>
                      </span>
                      <p className="mt-2">
                        By clicking Complete Checkout, you authorize a one-time charge in US Dollars for the amount shown above.
                        Shipping is free on orders over ${FREE_SHIPPING_THRESHOLD}. All orders are delivered via {SHIPPING_METHOD};
                        please allow {SHIPPING_TIME} for delivery within the United States.
                      </p>
                      <p className="mt-2">
                        Charges will appear on your statement as{' '}
                        <span className="font-semibold text-neutral-900">"{CARD_DESCRIPTOR}"</span>. If you have any questions,
                        please contact customer service at{' '}
                        <a
                          href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                          className="text-copper-600 hover:underline font-medium"
                        >
                          {CONTACT_INFO.phone}
                        </a>{' '}
                        or email us at{' '}
                        <a href={`mailto:${CONTACT_INFO.email}`} className="text-copper-600 hover:underline font-medium">
                          {CONTACT_INFO.email}
                        </a>
                        .
                      </p>
                    </div>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isProcessing || !agreedToTerms}
                  className="w-full py-4 bg-neutral-900 hover:bg-copper-600 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="complete-checkout-btn"
                >
                  {isProcessing ? (
                    <span>Processing...</span>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      Complete Checkout - ${total.toFixed(2)}
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-2">
              <div className="bg-white border border-neutral-200 rounded-2xl p-6 sticky top-32">
                <h2 className="font-semibold text-neutral-900 text-lg mb-4">Order Summary</h2>

                <div className="space-y-4 mb-6">
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex gap-4">
                      <div className="w-16 h-16 bg-neutral-100 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={item.image} alt={item.name} className="w-full h-full object-contain p-2" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-neutral-900 text-sm truncate">{item.name}</p>
                        <p className="text-neutral-500 text-sm">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-medium text-neutral-900">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                <div className="border-t border-neutral-200 pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-600">Subtotal</span>
                    <span className="font-medium">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-600">Shipping</span>
                    <span className="font-medium">{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
                  </div>
                  <div className="flex justify-between text-lg pt-3 border-t border-neutral-200">
                    <span className="font-semibold">Total (USD)</span>
                    <span className="font-bold">${total.toFixed(2)}</span>
                  </div>
                </div>

                {/* Card Statement Notice */}
                <div className="mt-6 pt-4 border-t border-neutral-200">
                  <p className="text-xs text-neutral-500 text-center">
                    Charges will appear as "
                    <span className="font-medium text-neutral-700">{CARD_DESCRIPTOR}</span>" on your statement
                  </p>
                </div>

                {/* Trust Badges */}
                <div className="mt-6 grid grid-cols-3 gap-3">
                  <div className="text-center">
                    <Lock className="w-4 h-4 text-neutral-700 mx-auto mb-1" />
                    <p className="text-xs text-neutral-600">Secure</p>
                  </div>
                  <div className="text-center">
                    <Truck className="w-4 h-4 text-neutral-700 mx-auto mb-1" />
                    <p className="text-xs text-neutral-600">Fast Ship</p>
                  </div>
                  <div className="text-center">
                    <ShieldCheck className="w-4 h-4 text-neutral-700 mx-auto mb-1" />
                    <p className="text-xs text-neutral-600">30-Day</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
